import React, { useMemo } from "react";
import { useI18n } from "../i18n/I18nContext";
import ServiceIcon from "./ServiceIcon";

const methodNames = ["get", "post", "put", "patch", "delete", "options", "head"];

const getSecurityNames = (spec, notSpecifiedLabel) => {
    const schemes = spec?.components?.securitySchemes || spec?.securityDefinitions || {};
    const names = Object.keys(schemes);
    if (names.length) return names.join(", ");

    if (!Array.isArray(spec?.security)) return notSpecifiedLabel;

    const securityNames = spec.security.reduce(
        (items, item) => items.concat(Object.keys(item || {})),
        []
    );

    return securityNames.length ? [...new Set(securityNames)].join(", ") : notSpecifiedLabel;
};

const extractEndpoints = (spec, labels) =>
    Object.entries(spec?.paths || {}).reduce((items, [route, pathItem]) => {
        if (!pathItem) return items;

        return items.concat(
            Object.entries(pathItem)
                .filter(([method]) => methodNames.includes(method.toLowerCase()))
                .map(([method, operation]) => ({
                    method: method.toUpperCase(),
                    route,
                    summary: operation?.summary || operation?.description || labels.fallbackDescription,
                    auth: getSecurityNames(
                        { ...spec, security: operation?.security || spec?.security },
                        labels.notSpecified
                    ),
                }))
        );
    }, []);

const ContractOverview = ({ error, loading, service, spec }) => {
    const { t } = useI18n();
    const endpointLabels = useMemo(
        () => ({
            fallbackDescription: t("contract.fallbackDescription"),
            notSpecified: t("contract.notSpecified"),
        }),
        [t]
    );
    const endpoints = useMemo(() => extractEndpoints(spec, endpointLabels), [endpointLabels, spec]);
    const schemas = useMemo(() => Object.keys(spec?.components?.schemas || {}), [spec]);
    const jsonPreview = useMemo(() => (spec ? JSON.stringify(spec, null, 2) : ""), [spec]);
    const format = spec?.openapi ? `OpenAPI ${spec.openapi}` : spec?.swagger ? `Swagger ${spec.swagger}` : "OpenAPI";

    if (error) return <div className="contract-error">{error}</div>;

    if (loading || !spec) {
        return (
            <section className="contract-loading">
                <span className="auth-spinner" aria-hidden="true" />
                <p>{t("contract.loading")}</p>
            </section>
        );
    }

    return (
        <section className="contract-workspace">
            <div className="contract-main-panel">
                <div className="contract-title-row">
                    <div>
                        <h2>{spec.info?.title || service?.shortName}</h2>
                        <p>{spec.info?.description || (service ? t(`services.${service.key}.description`) : "")}</p>
                    </div>
                    <span>{format}</span>
                </div>

                <div className="contract-stats">
                    {[
                        ["endpoints", endpoints.length, "endpoints"],
                        ["schemas", schemas.length, "schemas"],
                        ["auth", getSecurityNames(spec, t("contract.notSpecified")), "auth"],
                        ["version", spec.info?.version || service?.version, "version"],
                    ].map(([label, value, icon]) => (
                        <article key={label}>
                            <span className="contract-stat-label">{t(`contract.${label}`)}</span>
                            <div className="contract-stat-value">
                                <strong>{value}</strong>
                                <span className="contract-stat-icon">
                                    <ServiceIcon name={icon} />
                                </span>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="contract-section-title">
                    <h3>{t("contract.endpoints")}</h3>
                </div>
                <div className="endpoint-table" role="table" aria-label={t("contract.contractTable")}>
                    <div className="endpoint-row endpoint-row-head" role="row">
                        <span>{t("contract.method")}</span>
                        <span>{t("contract.path")}</span>
                        <span>{t("contract.summary")}</span>
                        <span>{t("contract.auth")}</span>
                    </div>
                    {endpoints.map((endpoint) => (
                        <div className="endpoint-row" role="row" key={`${endpoint.method}-${endpoint.route}`}>
                            <strong className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                                {endpoint.method}
                            </strong>
                            <code>{endpoint.route}</code>
                            <span>{endpoint.summary}</span>
                            <span>{endpoint.auth}</span>
                        </div>
                    ))}
                </div>

                <div className="contract-section-title">
                    <h3>{t("contract.schemas")}</h3>
                    <span>{schemas.length}</span>
                </div>
                <div className="schema-grid">
                    {schemas.slice(0, 8).map((schema) => (
                        <article key={schema}>
                            <span>{schema}</span>
                            <strong>{t("contract.object")}</strong>
                        </article>
                    ))}
                    {!schemas.length ? <p>{t("contract.noSchemas")}</p> : null}
                </div>
            </div>

            <aside className="json-preview-panel">
                <div className="json-preview-header">
                    <h3>{t("contract.openApiJson")}</h3>
                    <span>{format}</span>
                </div>
                <pre className="json-code">
                    <code>{jsonPreview}</code>
                </pre>
            </aside>
        </section>
    );
};

export default ContractOverview;
